import type { ColumnDefinition } from "@frui.ts/dataviews";
import { DataTable } from "@frui.ts/dataviews";
import { Router } from "@frui.ts/screens";
import { View } from "@frui.ts/views";
import type { ViewComponent } from "@frui.ts/views/dist";
import { registerView } from "@frui.ts/views/dist";
import classNames from "classnames";
import Button from "@edaz/edazkit/Button/index";
import HeaderRow from "@edaz/edazkit/HeaderRow/index";
import { IconType } from "@edaz/edazkit/Icon";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import { Observer, observer } from "mobx-react-lite";
import React from "react";
import { Badge } from "react-bootstrap";
import UsersViewModel from "viewModels/users/users/usersViewModel";
import type User from "../../../entities/user";
import { listColumns } from "../../common/list";
import Paginator from "../../common/paginator";
import { formatPhoneNumber } from "utils/localizations";
import { SearchBarView } from "./searchBarView";
import { emptyValue } from "utils/emptyValue";

const UsersView: ViewComponent<UsersViewModel> = observer(({ vm }) => {
  const getUrl = Router.getChildUrlFactory(vm);

  const columns: ColumnDefinition<User>[] = [
    {
      title: `${vm.translateAttribute("firstName")} ${vm.translateAttribute("lastName")}`,
      property: "firstName",
      valueFormatter: props => {
        return (
          <a href={getUrl(props.item.id)} className="text-primary link-underline">
            {props.item.firstName} {props.item.lastName}
          </a>
        );
      },
    },
    {
      title: vm.translateAttribute("email"),
      property: "email",
    },
    {
      title: vm.translateAttribute("phoneNumber"),
      property: "phoneNumber",
      valueFormatter: props => (props.value ? formatPhoneNumber(props.value) : emptyValue),
    },
    {
      property: "isActive",
      valueFormatter: props => {
        if (!props.value && props.item.invitation && props.item.invitation.expiresAt < new Date()) {
          return (
            <Badge variant="danger" className="p-2">
              {vm.translate("invitationExpired")}
            </Badge>
          );
        } else if (!props.value) {
          return (
            <Badge variant="warning" className="p-2">
              {vm.translate("userInactive")}
            </Badge>
          );
        } else {
          return null;
        }
      },
    },
  ];

  const onAddClick = vm.onAddClick();
  const columnsProps = listColumns<User>({ vm, columns });

  if (vm.activeChild) {
    return <Observer>{() => <View vm={vm.activeChild} />}</Observer>;
  }

  return (
    <>
      <HeaderRow level={3} title={vm.name} className="mb-4">
        {onAddClick && (
          <Button className="rounded" size="sm" iconLeft={IconType.SignPlus} onClick={onAddClick}>
            {vm.translateGeneral("add")}
          </Button>
        )}
      </HeaderRow>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        {vm instanceof UsersViewModel && <SearchBarView vm={vm} />}
        <nav className="nav nav-tabs">
          <a
            href={`#/${vm.getNavigationPath().path ?? ""}?providerKind=ALL`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              vm.selectProvider();
            }}
            className={classNames("nav-item", "nav-link", {
              active: vm.appliedFilter?.providerKind === undefined,
            })}>
            {vm.translate("all")}
          </a>
          <a
            href={`#/${vm.getNavigationPath().path ?? ""}?providerKind=EXTERNAL`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              vm.selectProvider("EXTERNAL");
            }}
            className={classNames("nav-item", "nav-link", {
              active: vm.appliedFilter?.providerKind === "EXTERNAL",
            })}>
            {vm.translate("external")}
          </a>
          <a
            href={`#/${vm.getNavigationPath().path ?? ""}?providerKind=INTERNAL`}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              vm.selectProvider("INTERNAL");
            }}
            className={classNames("nav-item", "nav-link", {
              active: vm.appliedFilter?.providerKind === "INTERNAL",
            })}>
            {vm.translate("internal")}
          </a>
        </nav>
        <div className="table-full" style={{ overflowX: "auto" }}>
          <DataTable
            headerRowClassName="bg-white"
            columns={columnsProps}
            items={vm.items}
            itemKey={vm.idProperty}
            className="table table-striped"
          />
          <Paginator
            className="justify-content-center mt-4 mb-2"
            pagingInfo={vm.currentPaging}
            filter={vm.pagingFilter}
            onPageChanged={vm.loadData}
          />
        </div>
      </LoaderOverlay>
    </>
  );
});

registerView(UsersView, UsersViewModel);
